import {
  deleteElement,
  insertElement,
  updateIndexList,
} from "../../common/helpers/Utils";
import { emptyTag, generateSafeAlarmIds, getMaxTagIdentifier } from "./Helpers";
import { mergeIncomingTags, updateDirtyTags } from "./TagGridOperations";

/**
 * The tag grid's state. The values are matched by row index: `tags[i]` is the saved baseline of
 * `editedTags[i]`, and the two sets hold row indexes.
 * @typedef {object} TagGridState
 * @property {object[]} tags Saved baseline for each row.
 * @property {object[]} editedTags Rows as shown in the grid.
 * @property {Set<number>} dirtyTags Rows that differ from their saved baseline.
 * @property {Set<number>} selectedTags
 */

/** @returns {TagGridState} */
export const createEmptyTagGrid = () => ({
  tags: [],
  editedTags: [],
  dirtyTags: new Set(),
  selectedTags: new Set(),
});

const removeIndexFromList = (list, index) => {
  const remaining = new Set(list);
  remaining.delete(index);
  return updateIndexList(remaining, index, -1);
};

const insertRow = (state, row, at) => {
  const editedTags = [...state.editedTags];
  const tags = [...state.tags];
  insertElement(editedTags, row, at);
  insertElement(tags, row, at);
  return {
    ...state,
    editedTags,
    tags,
    dirtyTags: updateIndexList(state.dirtyTags, at, 1),
    selectedTags: updateIndexList(state.selectedTags, at, 1),
  };
};

const removeRowsAt = (state, indices) => {
  const editedTags = [...state.editedTags];
  const tags = [...state.tags];
  let dirtyTags = state.dirtyTags;
  for (const index of [...indices].sort((a, b) => b - a)) {
    deleteElement(editedTags, index);
    deleteElement(tags, index);
    dirtyTags = removeIndexFromList(dirtyTags, index);
  }
  return { ...state, editedTags, tags, dirtyTags };
};

const UNLINKED_ON_DUPLICATE = [
  "AssetTagId",
  "TagId",
  "TCTagLayerId",
  "AssetId",
  "AssetNumber",
];

const duplicateOf = (editedTags, at) => {
  const dupe = { ...editedTags[at] };
  for (const field of UNLINKED_ON_DUPLICATE) {
    if (dupe[field]) {
      delete dupe[field];
    }
  }
  dupe.TagIdentifier = getMaxTagIdentifier(editedTags) + 1;
  const { safeAlarmId, safeAlarm2Id } = generateSafeAlarmIds(dupe, editedTags);
  dupe.AlarmId = safeAlarmId;
  dupe.Alarm2Id = safeAlarm2Id;
  return dupe;
};

// Updaters get a copy: TagComparisonModal writes into the array it is handed.
const resolveUpdate = (value, copyOfCurrent) =>
  typeof value === "function" ? value(copyOfCurrent) : value;

/**
 * @param {TagGridState} state
 * @param {{ type: string }} action
 * @returns {TagGridState}
 */
export const tagGridReducer = (state, action) => {
  switch (action.type) {
    case "replaceGrid":
      return {
        tags: action.tags,
        editedTags: action.editedTags,
        dirtyTags: new Set(),
        selectedTags: new Set(),
      };
    case "restoreSession":
      return {
        tags: action.session.tags || [],
        editedTags: action.session.editedTags || [],
        dirtyTags: action.session.dirtyTags || new Set(),
        selectedTags: action.session.selectedTags || new Set(),
      };
    case "appendTags":
      return {
        ...state,
        ...mergeIncomingTags({
          editedTags: state.editedTags,
          tags: state.tags,
          incoming: action.tags,
          collisionMode: action.collisionMode,
        }),
      };
    case "insertEmpty":
      return insertRow(state, emptyTag, action.at);
    case "duplicate":
      return insertRow(
        state,
        duplicateOf(state.editedTags, action.at),
        action.at + 1
      );
    case "removeRow":
      return {
        ...removeRowsAt(state, [action.index]),
        selectedTags: removeIndexFromList(state.selectedTags, action.index),
      };
    case "removeRows": {
      const indices = [...new Set(action.indices)].filter(
        (index) =>
          Number.isInteger(index) &&
          index >= 0 &&
          index < state.editedTags.length
      );
      if (indices.length === 0) {
        return state;
      }
      return { ...removeRowsAt(state, indices), selectedTags: new Set() };
    }
    case "updateRow": {
      const editedTags = [...state.editedTags];
      editedTags[action.index] = action.row;
      return {
        ...state,
        editedTags,
        dirtyTags: updateDirtyTags({
          dirtyTags: state.dirtyTags,
          editedTags,
          tags: state.tags,
          indices: [action.index],
        }),
      };
    }
    case "editRows":
      return {
        ...state,
        editedTags: action.editedTags,
        dirtyTags: updateDirtyTags({
          dirtyTags: state.dirtyTags,
          editedTags: action.editedTags,
          tags: state.tags,
          indices: action.indices,
        }),
      };
    case "applyEdits":
      return {
        ...state,
        editedTags: action.editedTags,
        dirtyTags: action.dirtyTags,
      };
    case "reconcileSave": {
      const savedRowByBaseline = new Map();
      for (const [savedRow, baseline] of action.savedRows) {
        if (baseline) {
          savedRowByBaseline.set(baseline, savedRow);
        }
      }
      const tags = [...state.tags];
      const savedIndices = [];
      state.editedTags.forEach((row, index) => {
        const savedRow = action.savedRows.has(row)
          ? row
          : savedRowByBaseline.get(state.tags[index]);
        if (savedRow) {
          tags[index] = savedRow;
          savedIndices.push(index);
        }
      });
      return {
        ...state,
        tags,
        dirtyTags: updateDirtyTags({
          dirtyTags: state.dirtyTags,
          editedTags: state.editedTags,
          tags,
          indices: savedIndices,
        }),
      };
    }
    case "reset":
      return { ...state, editedTags: state.tags, dirtyTags: new Set() };
    case "clear":
      return createEmptyTagGrid();
    case "setEditedTags":
      return {
        ...state,
        editedTags: resolveUpdate(action.value, [...state.editedTags]),
      };
    case "setDirtyTags":
      return {
        ...state,
        dirtyTags: resolveUpdate(action.value, new Set(state.dirtyTags)),
      };
    case "setSelectedTags":
      return {
        ...state,
        selectedTags: resolveUpdate(action.value, new Set(state.selectedTags)),
      };
    default:
      throw new Error(`Unknown tag grid action: ${action.type}`);
  }
};
